import { debounce } from 'lodash';
import {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { MultiGrid } from 'react-virtualized';

import { DatasetGridViewModel } from '../../../../models/dataset/DatasetRelated';
import { DatasetGridColumn } from '../../../../services/dataset/view-model/DatasetGridColumn';
import { SearchInputProps } from '../components/SearchInput';
import { FEATURES, GRID_MODE, SEARCH_DIRECTIONS } from '../constants';
import { DatasetGridProps } from '../DatasetGrid';
import { copyTextToClipboard } from '../utils/copyTextToClipboard';
import {
  getFormattedActiveCellValue,
  IntlFormatter,
} from '../utils/getFormattedValue';
import { getShortCutCombinationStrings } from '../utils/getShortCutCombinationStrings';

interface Props
  extends
    Pick<
      DatasetGridProps,
      | 'columns'
      | 'keyMap'
      | 'labelTitle'
      | 'onChangeSelectedIds'
      | 'onInitiateCommand'
      | 'rowIdField'
      | 'rows'
    >,
    Pick<SearchInputProps, 'onCloseSearchInput' | 'onSearch'> {
  activeCell: number;
  activeRow: number;
  features: FEATURES[];
  gridRef: MutableRefObject<MultiGrid | null>;
  mode: GRID_MODE;
  orderedColumns: DatasetGridColumn[];
  showSearchInput: boolean;
  visibleRows: number;
  intlFormatter: IntlFormatter;
  inverseSelection: () => void;
  setActiveRow: Dispatch<SetStateAction<number>>;
  setInternalActiveRow: (index: number) => void;
  setActiveCell: Dispatch<SetStateAction<number>>;
  setIsPopoverOpen: Dispatch<SetStateAction<boolean>>;
  setShouldDisableScroll: Dispatch<SetStateAction<boolean>>;
  onActiveChange: (id: number) => void;
  onChangeSelectedIds: (ids: number[]) => void;
  setShowSearchInput: Dispatch<SetStateAction<boolean>>;
  toggleAll: () => void;
  toggleCheckbox: (index: number) => void;
  editableFields: DatasetGridViewModel['editableFields'];
  setClickedCell: (cell: [number, number]) => void;
  setMode: Dispatch<SetStateAction<GRID_MODE>>;
  onCopySuccess: () => void;
}

const useKeyDown = (
  ref: RefObject<HTMLElement>,
  {
    activeCell,
    activeRow,
    columns,
    editableFields,
    features,
    gridRef,
    keyMap,
    labelTitle,
    mode,
    orderedColumns,
    rowIdField,
    rows,
    showSearchInput,
    visibleRows,
    intlFormatter,
    inverseSelection,
    onActiveChange,
    onChangeSelectedIds,
    onCloseSearchInput,
    onInitiateCommand,
    onSearch,
    setActiveCell,
    setActiveRow,
    setInternalActiveRow,
    setClickedCell,
    setIsPopoverOpen,
    setMode,
    setShouldDisableScroll,
    setShowSearchInput,
    toggleAll,
    toggleCheckbox,
    onCopySuccess,
  }: Props,
  stateOn: boolean
): void => {
  const setActiveDebounce = (setActive: Dispatch<SetStateAction<number>>) =>
    debounce(setActive, 5, { maxWait: 0 });

  const currentRowId = useMemo((): string => {
    const row = rows[activeRow];

    if (!row) {
      return '';
    }
    return row[rowIdField];
  }, [activeRow, rowIdField, rows]);

  const enableScroll = useCallback(
    (code: string, ctrlKey: boolean, key: string) => {
      const singleKeyboardCodesWithWorkedScroll = ['Space'];
      const singleKeyboardKeysWithWorkedScroll = [
        'ArrowUp',
        'ArrowDown',
        'ArrowRight',
        'ArrowLeft',
        'PageUp',
        'PageDown',
      ];
      const doubleKeyboardKeysWithWorkedScroll = ['End', 'Home'];
      if (
        singleKeyboardCodesWithWorkedScroll.includes(code) ||
        singleKeyboardKeysWithWorkedScroll.includes(key) ||
        (doubleKeyboardKeysWithWorkedScroll.includes(code) && ctrlKey)
      ) {
        setShouldDisableScroll(false);
      }
    },
    [setShouldDisableScroll]
  );

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent) => {
      const { code, ctrlKey, key, metaKey, altKey } = event;

      if (mode === GRID_MODE.EDIT) {
        return;
      }

      if (
        (code === 'Slash' || (ctrlKey && code === 'KeyF')) &&
        !showSearchInput
      ) {
        setShowSearchInput(true);
        event.preventDefault();
      }

      if (showSearchInput) {
        const rowsCopy = [...rows];
        switch (key) {
          case 'ArrowUp':
            event.preventDefault();
            if (activeRow > 0) {
              onSearch(
                event,
                rowsCopy.slice(0, activeRow).reverse(),
                SEARCH_DIRECTIONS.PREV
              );
            }
            break;
          case 'ArrowDown':
            event.preventDefault();
            if (activeRow < rows.length - 1) {
              onSearch(
                event,
                rowsCopy.slice(activeRow + 1),
                SEARCH_DIRECTIONS.NEXT
              );
            }
            break;
          case 'Escape':
            event.stopImmediatePropagation();
            onCloseSearchInput();
            break;
        }
        return;
      }

      enableScroll(code, ctrlKey, key);

      switch (key) {
        case 'ArrowUp':
          if (activeRow > 0) {
            setActiveDebounce(setActiveRow)(activeRow - 1);
          }
          break;
        case 'ArrowDown':
          if (activeRow < rows.length - 1) {
            setActiveDebounce(setActiveRow)(activeRow + 1);
          }
          break;
        case 'ArrowRight':
          if (
            activeCell <
            (features.includes(FEATURES.LABEL) && labelTitle
              ? columns.length - 2
              : columns.length - 1)
          ) {
            setActiveDebounce(setActiveCell)(activeCell + 1);
          }
          break;
        case 'ArrowLeft':
          if (activeCell > 0) {
            setActiveDebounce(setActiveCell)(activeCell - 1);
          } else if ((gridRef.current as MultiGrid).state.scrollLeft > 0) {
            setActiveDebounce(setActiveCell)(-1);
          }
          break;
        case 'End':
          if (ctrlKey) {
            setActiveDebounce(setActiveRow)(rows.length - 1);
          }
          break;
        case 'F7':
          event.preventDefault();
          setIsPopoverOpen(true);
          // TODO refactor with unified way of checking hotkeys conflicts
          return;
        case 'Home':
          if (ctrlKey) {
            setActiveDebounce(setActiveRow)(0);
          }
          break;
        case 'PageUp': {
          const upIndex = activeRow - visibleRows;
          setActiveDebounce(setActiveRow)(Math.max(upIndex, 0));
          break;
        }
        case 'PageDown': {
          const downIndex = activeRow + visibleRows;
          setActiveDebounce(setActiveRow)(
            downIndex > rows.length ? rows.length - 1 : downIndex
          );
          break;
        }
        case 'Enter': {
          const { field: columnName } = orderedColumns[activeCell] ?? {};
          const editableFieldConfig = editableFields?.[columnName];
          if (editableFieldConfig) {
            setClickedCell([activeRow, activeCell]);
            setMode(GRID_MODE.EDIT);
          }
          break;
        }
      }

      switch (code) {
        case 'NumpadSubtract':
          onChangeSelectedIds([]);
          break;
        case 'NumpadAdd':
          toggleAll();
          break;
        case 'NumpadMultiply':
          inverseSelection();
          break;
        case 'Space': {
          event.preventDefault();
          toggleCheckbox(activeRow);
          const nextRow = activeRow + 1;
          if (nextRow < rows.length) {
            setInternalActiveRow(nextRow);
          }
          break;
        }
        case 'KeyC':
        case 'Insert':
          if (ctrlKey || metaKey) {
            if (!rows[activeRow]) break;
            try {
              if (altKey) {
                event.preventDefault();
                await copyTextToClipboard(currentRowId);
              } else {
                await copyTextToClipboard(
                  getFormattedActiveCellValue(
                    orderedColumns,
                    activeCell,
                    rows,
                    activeRow,
                    intlFormatter
                  )
                );
              }
              onCopySuccess();
            } catch {
              break;
            }
          }
          break;
      }
      if (!keyMap) {
        return;
      }
      const { codeCombinationString, keyCombinationString } =
        getShortCutCombinationStrings(event);
      const hotKeyMatched =
        (codeCombinationString in keyMap && codeCombinationString) ||
        (keyCombinationString in keyMap && keyCombinationString);
      if (hotKeyMatched) {
        event.preventDefault();
        onInitiateCommand(keyMap[hotKeyMatched]);
      }
    },
    [
      mode,
      showSearchInput,
      enableScroll,
      keyMap,
      setShowSearchInput,
      rows,
      activeRow,
      onCloseSearchInput,
      onSearch,
      activeCell,
      features,
      labelTitle,
      columns.length,
      gridRef,
      setIsPopoverOpen,
      setActiveRow,
      setInternalActiveRow,
      setActiveCell,
      visibleRows,
      orderedColumns,
      editableFields,
      setClickedCell,
      setMode,
      onChangeSelectedIds,
      toggleAll,
      inverseSelection,
      toggleCheckbox,
      intlFormatter,
      currentRowId,
      onInitiateCommand,
      onCopySuccess,
    ]
  );

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (mode === GRID_MODE.EDIT) {
        return;
      }

      if (event.code === 'Space' && rows[activeRow]) {
        onActiveChange(rows[activeRow][rowIdField]);
      }
    },
    [mode, rows, activeRow, rowIdField, onActiveChange]
  );

  useEffect(() => {
    const { current: element } = ref;
    if (stateOn) {
      element?.addEventListener('keydown', handleKeyDown);
      element?.addEventListener('keyup', handleKeyUp);
      return () => {
        element?.removeEventListener('keydown', handleKeyDown);
        element?.removeEventListener('keyup', handleKeyUp);
      };
    }
  }, [handleKeyDown, handleKeyUp, ref, stateOn]);
};

export default useKeyDown;
